import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import AppLayout, { PageLoader } from "../../components/AppLayout";
import { Button } from "../../components/ui/button";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!s) return <AppLayout title="Settings"><PageLoader /></AppLayout>;

  const num = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const tog = (k) => (e) => setS({ ...s, [k]: e.target.checked });

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/admin/settings", {
        capital_lock_days: Number(s.capital_lock_days),
        exit_load_pct: Number(s.exit_load_pct),
        profit_sharing_pct: Number(s.profit_sharing_pct),
        allow_partial_withdrawal: Boolean(s.allow_partial_withdrawal),
        allow_full_redemption: Boolean(s.allow_full_redemption),
      });
      setS(r.data);
      toast.success("Withdrawal engine configuration updated — audit logged.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout title="Settings" subtitle="Central withdrawal-engine configuration. Client-level overrides are set on each client's Investment Plan tab.">
      <div className="fade-up max-w-3xl">
        <div className="glass-card p-7 space-y-6" data-testid="settings-card">
          <div>
            <h3 className="font-heading font-semibold text-white">Capital Lock</h3>
            <p className="text-[11px] text-zinc-500 mt-1">Newly onboarded AUM stays locked for this many calendar days from the AUM Onboarded Date. Interest/profit follows its own eligibility cycle.</p>
            <div className="mt-3 max-w-xs space-y-1.5">
              <label className="label-caps">Capital Lock (calendar days)</label>
              <input type="number" value={s.capital_lock_days} onChange={num("capital_lock_days")}
                data-testid="settings-lock-days" className="glass-input" />
            </div>
          </div>
          <div className="border-t border-white/[0.06] pt-5">
            <h3 className="font-heading font-semibold text-white">Exit Load & Profit Sharing</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <div className="space-y-1.5">
                <label className="label-caps">Default Exit Load %</label>
                <input type="number" value={s.exit_load_pct} onChange={num("exit_load_pct")}
                  data-testid="settings-exit-load" className="glass-input" />
                <p className="text-[10px] text-zinc-600">Applied to capital withdrawals before maturity.</p>
              </div>
              <div className="space-y-1.5">
                <label className="label-caps">Default Profit Sharing %</label>
                <input type="number" value={s.profit_sharing_pct} onChange={num("profit_sharing_pct")}
                  data-testid="settings-profit-sharing" className="glass-input" />
                <p className="text-[10px] text-zinc-600">Applied to interest/profit (Return) withdrawals.</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/[0.06] pt-5 space-y-3">
            <h3 className="font-heading font-semibold text-white">Withdrawal Permissions</h3>
            <label className="flex items-center gap-2.5 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={!!s.allow_partial_withdrawal} onChange={tog("allow_partial_withdrawal")}
                data-testid="settings-allow-partial" className="accent-emerald-500" />
              Partial capital withdrawal permitted (after lock)
            </label>
            <label className="flex items-center gap-2.5 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={!!s.allow_full_redemption} onChange={tog("allow_full_redemption")}
                data-testid="settings-allow-full" className="accent-emerald-500" />
              Full redemption permitted (after lock)
            </label>
          </div>
          <Button onClick={save} disabled={busy} data-testid="settings-save"
            className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
            {busy ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
